import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, Check, Mail, Phone } from "lucide-react";
import { Reveal, SectionLabel, OPS_OPTION, openSchedule } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUILDING_OPTIONS = [
  "New Restaurant", "New Food Brand", "Existing Kitchen Optimisation", "Expansion", "Menu / Operations", OPS_OPTION, "Other",
];

const TIMELINE_OPTIONS = [
  "Immediately — within 30 days", "In 1–3 months", "In 3–6 months", "Just exploring for now",
];

const COUNTRY_CODES = ["+91", "+1", "+44", "+61", "+65", "+971", "+974", "+966"];

const phoneCls = "w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300 placeholder:text-ink/25";

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">{label}</span>
    {children}
    {error && <span className="block mt-1.5 text-xs text-red-400" data-testid={`error-${label.toLowerCase().replace(/[\s/]+/g, "-")}`}>{error}</span>}
  </label>
);

const inputCls = "mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300 placeholder:text-ink/25";

const Contact = () => {
  const [form, setForm] = useState({
    name: "", email: "", phoneCode: "+91", phone: "", altPhoneCode: "+91", altPhone: "",
    brand: "", city: "", building: "", timeline: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, building: e.detail }));
    window.addEventListener("skope:preselect-building", handler);
    return () => window.removeEventListener("skope:preselect-building", handler);
  }, []);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!/^[0-9]{10}$/.test(form.phone)) e.phone = "Enter your 10-digit number.";
    if (form.altPhone && !/^[0-9]{10}$/.test(form.altPhone)) e.altPhone = "Enter a 10-digit number or leave this empty.";
    if (form.city.trim().length < 2) e.city = "Please enter your city.";
    if (!form.building) e.building = "Please select an option.";
    if (form.message.trim().length < 10) e.message = "Tell us a little more (min 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, brand: form.brand, city: form.city,
        building: form.building, timeline: form.timeline, message: form.message,
        phone: `${form.phoneCode} ${form.phone}`,
        altPhone: form.altPhone ? `${form.altPhoneCode} ${form.altPhone}` : "",
      });
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setServerError(err.response?.data?.detail || "Something went wrong. Please email contact@skopekitchens.com directly.");
    }
  };

  return (
    <section id="contact" className="relative bg-[#f6f6f6] py-28 lg:py-40 border-t border-ink/5" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal><SectionLabel>Contact — 10</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="contact-headline">
              TALK TO US BEFORE YOU BUILD.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-ink/55 leading-relaxed max-w-md">
              The most expensive kitchen mistakes are made before the kitchen exists.
              A conversation now costs nothing — a wrong layout costs years.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-5">
              <a href="mailto:contact@skopekitchens.com" className="flex items-center gap-4 text-ink/70 hover:text-skope transition-colors group" data-testid="contact-email-link">
                <span className="w-10 h-10 border border-ink/15 group-hover:border-skope flex items-center justify-center transition-colors"><Mail size={16} /></span>
                <span className="font-mono text-sm tracking-[0.05em]">contact@skopekitchens.com</span>
              </a>
              <a href="tel:+918618689724" className="flex items-center gap-4 text-ink/70 hover:text-skope transition-colors group" data-testid="contact-phone-link">
                <span className="w-10 h-10 border border-ink/15 group-hover:border-skope flex items-center justify-center transition-colors"><Phone size={16} /></span>
                <span className="font-mono text-sm tracking-[0.05em]">+91 8618689724</span>
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="mt-10 border border-skope/40 bg-skope/[0.05] p-6" data-testid="contact-schedule-option">
              <div className="font-mono text-[10px] tracking-[0.25em] text-skope uppercase">Option 01 — For a direct conversation</div>
              <p className="mt-2 text-sm text-ink/55">I have a food business or idea and want to speak with Skope.</p>
              <button
                onClick={() => openSchedule()}
                className="mt-4 w-full flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-ink hover:text-white transition-colors duration-300"
                data-testid="contact-schedule-cta"
              >
                Schedule a Call <ArrowRight size={14} />
              </button>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            {status === "sent" ? (
              <div className="border border-skope/40 bg-skope/[0.04] p-10 lg:p-14" data-testid="contact-success">
                <span className="w-12 h-12 border border-skope flex items-center justify-center"><Check size={20} className="text-skope" /></span>
                <h3 className="mt-6 font-display font-semibold text-2xl text-ink">Thank you — we've received your enquiry.</h3>
                <p className="mt-3 text-ink/55 leading-relaxed max-w-md">
                  Our team will review what you're building and get back to you shortly.
                  If it's urgent, write to <a href="mailto:contact@skopekitchens.com" className="text-skope underline underline-offset-4">contact@skopekitchens.com</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="border border-ink/10 bg-white p-8 lg:p-12" data-testid="contact-form">
                <div className="mb-8">
                  <div className="font-mono text-[10px] tracking-[0.25em] text-ink/40 uppercase">Option 02 — Send an enquiry</div>
                  <p className="mt-1.5 text-sm text-ink/50">For questions, partnerships or anything else.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Name *" error={errors.name}>
                    <input value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="form-name" />
                  </Field>
                  <Field label="Email *" error={errors.email}>
                    <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" data-testid="form-email" />
                  </Field>
                  <Field label="Primary Phone *" error={errors.phone}>
                    <div className="mt-2 flex gap-3">
                      <select value={form.phoneCode} onChange={set("phoneCode")} aria-label="Country code" data-testid="form-phone-code"
                        className="w-24 shrink-0 bg-white border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300">
                        {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input value={form.phone} onChange={set("phone")} inputMode="numeric" maxLength={10} placeholder="10-digit mobile number" data-testid="form-phone"
                        className="flex-1 min-w-0 bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300 placeholder:text-ink/30" />
                    </div>
                  </Field>
                  <Field label="Alternate Phone (optional)" error={errors.altPhone}>
                    <div className="mt-2 flex gap-3">
                      <select value={form.altPhoneCode} onChange={set("altPhoneCode")} aria-label="Alternate country code" data-testid="form-alt-phone-code"
                        className="w-24 shrink-0 bg-white border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300">
                        {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input value={form.altPhone} onChange={set("altPhone")} inputMode="numeric" maxLength={10} placeholder="10-digit number (optional)" data-testid="form-alt-phone"
                        className="flex-1 min-w-0 bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors duration-300 placeholder:text-ink/30" />
                    </div>
                  </Field>
                  <Field label="Business / Brand" error={errors.brand}>
                    <input value={form.brand} onChange={set("brand")} className={inputCls} placeholder="Brand or company name" data-testid="form-brand" />
                  </Field>
                  <Field label="City *" error={errors.city}>
                    <input value={form.city} onChange={set("city")} className={inputCls} placeholder="Where are you building?" data-testid="form-city" />
                  </Field>
                  <Field label="What are you building? *" error={errors.building}>
                    <select value={form.building} onChange={set("building")} className={`${inputCls} bg-white`} data-testid="form-building">
                      <option value="" disabled>Select an option</option>
                      {BUILDING_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="How fast do you want to start with us?">
                    <select value={form.timeline} onChange={set("timeline")} className={`${inputCls} bg-white`} data-testid="form-timeline">
                      <option value="" disabled>Select a timeline</option>
                      {TIMELINE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="mt-8">
                  <Field label="Message *" error={errors.message}>
                    <textarea rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your concept, menu, volumes or plans…" data-testid="form-message" />
                  </Field>
                </div>
                {serverError && <p className="mt-6 text-sm text-red-400" data-testid="form-server-error">{serverError}</p>}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="group mt-10 flex items-center justify-center gap-3 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-10 py-4 hover:bg-ink hover:text-white transition-colors duration-300 disabled:opacity-50 w-full sm:w-auto"
                  data-testid="form-submit"
                >
                  {status === "sending" ? "Sending…" : "Send Enquiry"}
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
